// Niveau 5 — Shift, store bogstaver og tegn

import type { CurriculumTier, Trin } from '../../types';
import { DICTATION_FIRST_NAMES } from '../dictationNames';
import { NIVEAU_1_SPRINT_WORDS } from './niveau-1';
import { NIVEAU_2_SPRINT_WORDS } from './niveau-2';
import { NIVEAU_3_SPRINT_WORDS } from './niveau-3';
import { NIVEAU_4_ALPHABET_WORDS, NIVEAU_4_UNLOCKED_KEYS } from './niveau-4';
import {
  filterWords,
  fromChunks,
  fromLetters,
  interleaved,
  literal,
  literalOneOf,
  round,
} from './generators';
import { consolidationTrin, pairTrin } from './trinBuilders';

function upperForKey(k: string): string {
  if (k === 'æ') return 'Æ';
  if (k === 'ø') return 'Ø';
  if (k === 'å') return 'Å';
  if (k.length === 1 && k >= 'a' && k <= 'z') return k.toUpperCase();
  return k;
}

const LETTER_LOWER = NIVEAU_4_UNLOCKED_KEYS.filter(
  (k) => k.length === 1 && k !== ' ' && !',.;-'.includes(k),
);
const ALL_UPPER = LETTER_LOWER.map(upperForKey);

let prior = [...NIVEAU_4_UNLOCKED_KEYS];

const HOME_UPPER = ['A', 'S', 'D', 'F', 'J', 'K', 'L', 'Æ', 'Ø'] as const;
const TOP_UPPER = ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P', 'Å'] as const;
const BOTTOM_UPPER = ['Z', 'X', 'C', 'V', 'B', 'N', 'M', 'G', 'H'] as const;

function shiftLetterPool(uppers: readonly string[]): string[] {
  return [...uppers, ...uppers.map((c) => c.toLowerCase())];
}

const t1: Trin = {
  id: 'niveau-5-trin-1',
  tierId: 'niveau-5',
  trinNumber: 1,
  title: 'Shift og hjemmerækken',
  description: 'Lær at holde Shift med lillefingeren og skrive store bogstaver.',
  goal: 'Venstre Shift til højre hånds bogstaver — højre Shift til venstre.',
  unlockedKeys: [...prior, ...HOME_UPPER],
  newKeys: [],
  strictCase: true,
  rounds: [
    round({
      id: 'niveau-5-trin-1-r1',
      title: 'Skift mellem små og store',
      purpose: 'Mærk Shift + samme tast.',
      mode: 'guided',
      generate: literal('a A a A s S s S d D d D f F f F'),
    }),
    round({
      id: 'niveau-5-trin-1-r2',
      title: 'Højre hånd',
      purpose: 'Shift med venstre lillefinger.',
      mode: 'guided',
      generate: literal('j J j J k K k K l L l L æ Æ æ Æ ø Ø ø Ø'),
    }),
    round({
      id: 'niveau-5-trin-1-r3',
      title: 'Blanding med store',
      purpose: 'Store og små bogstaver i flyd — korrekt case.',
      mode: 'recall',
      generate: literal('As Df Jk La Sd Fj Kl Æa Øl'),
    }),
    round({
      id: 'niveau-5-trin-1-r4',
      title: 'Blanding',
      purpose: 'Tilfældig rækkefølge.',
      mode: 'recall',
      generate: fromLetters(shiftLetterPool(HOME_UPPER), 2, 80),
    }),
    round({
      id: 'niveau-5-trin-1-r5',
      title: 'Mini-udfordring',
      purpose: 'Uden ledetråde.',
      mode: 'challenge',
      generate: literal('AsDf JkLæ FjKa SdLa DfJk ØlÆ'),
    }),
  ],
};
prior = t1.unlockedKeys;

const t2: Trin = {
  id: 'niveau-5-trin-2',
  tierId: 'niveau-5',
  trinNumber: 2,
  title: 'Shift og øverste række',
  description: 'Store bogstaver på Q W E R T Y U I O P og Å.',
  goal: 'Stræk opad med Shift — samme fingre som små bogstaver.',
  unlockedKeys: [...prior, ...TOP_UPPER.filter((u) => !prior.includes(u))],
  newKeys: [],
  strictCase: true,
  rounds: [
    round({
      id: 'niveau-5-trin-2-r1',
      title: 'Venstre hånd',
      purpose: 'Shift + E, R, T og W, Q.',
      mode: 'guided',
      generate: literal('e E e E r R r R t T t T w W w W q Q q Q'),
    }),
    round({
      id: 'niveau-5-trin-2-r2',
      title: 'Højre hånd',
      purpose: 'Shift + Y, U, I, O, P og Å.',
      mode: 'guided',
      generate: literal('y Y y Y u U u U i I i I o O o O p P p P å Å å Å'),
    }),
    round({
      id: 'niveau-5-trin-2-r3',
      title: 'Blanding med store',
      purpose: 'Store og små på øverste række — korrekt case.',
      mode: 'recall',
      generate: literal('Er Ty Ui Op Ål Qw Rt Yo Up Io'),
    }),
    round({
      id: 'niveau-5-trin-2-r4',
      title: 'Blanding',
      purpose: 'Tilfældig rækkefølge.',
      mode: 'recall',
      generate: fromLetters(shiftLetterPool(TOP_UPPER), 2, 80),
    }),
    round({
      id: 'niveau-5-trin-2-r5',
      title: 'Mini-udfordring',
      purpose: 'Uden ledetråde.',
      mode: 'challenge',
      generate: literal('QwEr TyUi OpÅl ErTy UiOp QpÅo'),
    }),
  ],
};
prior = t2.unlockedKeys;

const t3: Trin = {
  id: 'niveau-5-trin-3',
  tierId: 'niveau-5',
  trinNumber: 3,
  title: 'Shift og bundrækken',
  description: 'Store bogstaver på Z X C V B N M — og G og H på hjemmerækken.',
  goal: 'Stræk nedad med Shift — plus midtertasterne G og H.',
  unlockedKeys: [...prior, ...BOTTOM_UPPER.filter((u) => !prior.includes(u))],
  newKeys: [],
  strictCase: true,
  rounds: [
    round({
      id: 'niveau-5-trin-3-r1',
      title: 'Bundrækken',
      purpose: 'Shift + Z, X, C, V, B, N og M.',
      mode: 'guided',
      generate: literal('z Z z Z x X x X c C c C v V v V b B b B n N n N m M m M'),
    }),
    round({
      id: 'niveau-5-trin-3-r2',
      title: 'G og H',
      purpose: 'Midten af hjemmerækken med stort.',
      mode: 'guided',
      generate: literal('g G g G h H h H fg Gh Hg Fg gh GH hg'),
    }),
    round({
      id: 'niveau-5-trin-3-r3',
      title: 'Blanding med store',
      purpose: 'Store og små på bund og midte — korrekt case.',
      mode: 'recall',
      generate: literal('Zx Cv Bn Mx Gz Hj Vb Nm Gh Zc'),
    }),
    round({
      id: 'niveau-5-trin-3-r4',
      title: 'Blanding',
      purpose: 'Tilfældig rækkefølge.',
      mode: 'recall',
      generate: fromLetters(shiftLetterPool(BOTTOM_UPPER), 2, 80),
    }),
    round({
      id: 'niveau-5-trin-3-r5',
      title: 'Mini-udfordring',
      purpose: 'Uden ledetråde.',
      mode: 'challenge',
      generate: literal('ZxCv BnMx GhJz VbNm XcGh ZmBn'),
    }),
  ],
};
prior = t3.unlockedKeys;

const t4Keys = [...prior, ...ALL_UPPER.filter((u) => !prior.includes(u))];

const t4: Trin = {
  id: 'niveau-5-trin-4',
  tierId: 'niveau-5',
  trinNumber: 4,
  title: 'Sætningsstart',
  description: 'Store bogstaver i ordstart og efter punktum.',
  goal: 'Skriv Jeg, Det og navne med stort — uden at kigge.',
  unlockedKeys: t4Keys,
  newKeys: [],
  strictCase: true,
  rounds: [
    round({
      id: 'niveau-5-trin-4-r1',
      title: 'Ord med stort',
      purpose: 'Almindelige ordstart.',
      mode: 'guided',
      generate: literal('Jeg Det Vi Han Hun De Danmark'),
    }),
    round({
      id: 'niveau-5-trin-4-r2',
      title: 'Korte sætninger',
      purpose: 'Stort efter punktum.',
      mode: 'guided',
      generate: literal('Jeg kan. Det er godt. Vi skal.'),
    }),
    round({
      id: 'niveau-5-trin-4-r3',
      title: 'Flash-genkald',
      purpose: 'Se kort — skriv med korrekt case.',
      mode: 'recall',
      generate: literal('Jeg kan skrive. Det er rart.'),
      presentation: { kind: 'echo', flashMs: 2000, unit: 'chunk' },
    }),
    round({
      id: 'niveau-5-trin-4-r4',
      title: 'Blanding',
      purpose: 'Små og store i samme runde.',
      mode: 'recall',
      generate: fromChunks(
        filterWords(
          [
            'Jeg', 'Det', 'Vi', 'Han', 'Hun', 'De', 'Du', 'Mor', 'Far', 'Skole',
            'Danmark', ...DICTATION_FIRST_NAMES,
          ],
          t4Keys,
        ),
        90,
        { maxRepeatPerChunk: 1 },
      ),
    }),
    round({
      id: 'niveau-5-trin-4-r5',
      title: 'Mini-udfordring',
      purpose: 'Uden hjælp.',
      mode: 'challenge',
      generate: literal('Jeg kan skrive ord. Det er godt.'),
    }),
  ],
};
prior = t4.unlockedKeys;

const t5 = pairTrin(
  'niveau-5',
  5,
  'Kolon og semikolon',
  'Tegn med Shift og punktum-tasterne.',
  'Shift + komma giver semikolon — Shift + punktum giver kolon.',
  [';', ':'],
  prior,
  {
    intro: '; : ; : ;: :; ; : ; :',
    connect: 'Det er: godt; fint; klart; næste.',
    recall1: 'Det er: godt; fint; klart.',
    recall2: 'Godt; fint: klart; næste.',
    challenge: 'Det er: godt; fint; klart; næste.',
  },
  { palette: 'echo' },
);
(t5 as Trin).strictCase = true;
prior = t5.unlockedKeys;

const t6 = pairTrin(
  'niveau-5',
  6,
  'Spørgsmål og udråb',
  'Spørgsmålstegn og udråbstegn med Shift.',
  'Shift + plus-tasten giver spørgsmålstegn på dansk tastatur.',
  ['?', '!'],
  prior,
  {
    intro: '? ! ? ! ?! !? ? ! ? !',
    connect: 'Hvad skal vi? Det er godt! Skal vi gå?',
    recall1: 'Hvad nu? Det er fint! Skal vi?',
    recall2: 'Er det godt? Ja! Kom nu!',
    challenge: 'Hvad skal vi? Det er godt! Kom nu!',
  },
  { palette: 'echo' },
);
(t6 as Trin).strictCase = true;
prior = t6.unlockedKeys;

const NIVEAU_5_CAPITAL_WORDS = [
  'Jeg', 'Det', 'Vi', 'Han', 'Hun', 'De', 'Du', 'Mor', 'Far', 'Skole', 'Danmark',
  'Klasse', 'Lærer', 'Elev', 'Sommer', 'Vinter',
];

const NIVEAU_5_COMMON_WORDS = [
  'side', 'med', 'og', 'det', 'er', 'en', 'til', 'på', 'for', 'kan', 'har', 'var',
  'god', 'ny', 'stor', 'lille', 'dag', 'nat', 'bog', 'ord', 'tekst', 'skrive', 'læse',
  'taste', 'finger', 'hånd', 'øve', 'træne', 'skole', 'land', 'mand', 'kvinde',
  'verden', 'arbejde', 'arbejder', 'familie', 'stadig', 'altid', 'meget', 'lidt',
  'helt', 'sige', 'siger', 'komme', 'vand', 'ting', 'sted', 'liv', 'skov', 'sprog',
  'børn', 'have', 'bliver', 'hele', 'store', 'bruge', 'bruger', 'start', 'mellem',
  'timer', 'opgave', 'svar', 'natur', 'vej', 'gade', 'lektion', 'dansk', 'prøve',
  'plan', 'brev', 'ven', 'mor', 'far', 'gerne', 'selv', 'nem', 'nemt', 'synes',
  'går', 'gik', 'kom', 'blev', 'skal', 'vil', 'nu', 'her', 'der', 'hos',
  'problem', 'projekt', 'center', 'cykel', 'skærm', 'træ', 'øvelse', 'bygning',
  'sommer', 'vinter', 'kraft', 'huset', 'vejen', 'klassen', 'læreren', 'eleven',
];

export const NIVEAU_5_CONSOLIDATION_SENTENCES = [
  'Jeg kan skrive sætninger nu. Det er godt!',
  'Freja læser en bog. Axel skriver en tekst.',
  'Vi skal på skole i dag. Er du klar?',
  'Mor og far kommer hjem. Det er dejligt!',
  'Hjalte bor i Danmark. Han kan skrive godt.',
  'Gitte og Jesper læser sammen. Det er hyggeligt!',
  'Sanne skriver et brev. Ole læser avisen.',
  'Inge og Knud arbejder hver dag. De kan godt skrive.',
  'Mette og Mads skal på skole. De glæder sig!',
  'Lone læser en god bog. Det er rart!',
  'Trine og Rasmus øver blindskrift. Det er sjovt!',
  'Bodil skriver en tekst. Viggo læser den højt.',
];

export const NIVEAU_5_MIXED_WORDS = filterWords(
  [
    ...NIVEAU_5_CAPITAL_WORDS,
    // Navne (dictation-names.json)
    ...DICTATION_FIRST_NAMES,
    ...NIVEAU_5_COMMON_WORDS,
  ],
  prior,
);

const CONSOLIDATION_SENTENCE = NIVEAU_5_CONSOLIDATION_SENTENCES[0]!;

const t7Base = consolidationTrin(
  'niveau-5',
  7,
  'Konsolidering',
  'Bland store bogstaver, tegn og hele alfabetet.',
  'Skriv rigtige danske sætninger med korrekt case.',
  prior,
  NIVEAU_5_MIXED_WORDS,
  CONSOLIDATION_SENTENCE,
  { maxRepeatPerChunk: 2 },
);

const t7MetronomeRound = round({
  id: 'niveau-5-trin-7-r5',
  title: 'Hold takten',
  purpose: 'Skriv én tast pr. slag — store bogstaver og tegn.',
  mode: 'recall',
  generate: literalOneOf(NIVEAU_5_CONSOLIDATION_SENTENCES),
  presentation: { kind: 'metronome', bpm: 55 },
});

const t7DictationWords = filterWords(
  [...NIVEAU_5_CAPITAL_WORDS, ...DICTATION_FIRST_NAMES],
  prior,
);

const t7: Trin = {
  ...t7Base,
  strictCase: true,
  rounds: [
    t7Base.rounds[0]!,
    t7Base.rounds[1]!,
    round({
      ...t7Base.rounds[2]!,
      generate: literalOneOf(NIVEAU_5_CONSOLIDATION_SENTENCES),
    }),
    t7Base.rounds[3]!,
    t7MetronomeRound,
    round({
      id: 'niveau-5-trin-7-r-dict',
      title: 'Diktat: ord med stort',
      purpose: 'Lyt og skriv — grå tegn (stort og tegnsætning) vises, men hører ikke med i lyden.',
      mode: 'recall',
      generate: fromChunks(
        t7DictationWords.length > 0 ? t7DictationWords : NIVEAU_5_MIXED_WORDS,
        60,
        { maxRepeatPerChunk: 1 },
      ),
      presentation: { kind: 'dictation' },
    }),
    round({
      id: 'niveau-5-trin-7-r7',
      title: 'Bland med niveau 1–4',
      purpose: 'Ord på tværs af alle niveauer.',
      mode: 'recall',
      generate: interleaved(
        [
          { chunks: NIVEAU_1_SPRINT_WORDS, weight: 1 },
          { chunks: NIVEAU_2_SPRINT_WORDS, weight: 1 },
          { chunks: NIVEAU_3_SPRINT_WORDS, weight: 1 },
          { chunks: NIVEAU_4_ALPHABET_WORDS, weight: 1 },
          { chunks: NIVEAU_5_MIXED_WORDS, weight: 2 },
        ],
        100,
      ),
    }),
    round({
      id: 'niveau-5-trin-7-r8',
      title: 'Diktat: hele sætningen',
      purpose: 'Lyt til hver sætning — grå tegn viser stort og tegnsætning.',
      mode: 'recall',
      generate: literalOneOf(NIVEAU_5_CONSOLIDATION_SENTENCES),
      presentation: { kind: 'dictation', dictationMode: 'paragraph', sentencePauseMs: 700 },
    }),
    round({
      id: 'niveau-5-trin-7-r9',
      title: 'Skygge — sætninger',
      purpose: 'Følg teksten i tempo.',
      mode: 'challenge',
      generate: fromChunks(NIVEAU_5_MIXED_WORDS, 90, { maxRepeatPerChunk: 2 }),
      presentation: { kind: 'shadow', cps: 4 },
    }),
    t7Base.rounds[t7Base.rounds.length - 1]!,
  ],
};

export const NIVEAU_5_UNLOCKED_KEYS = t7.unlockedKeys;
export const NIVEAU_5_SPRINT_WORDS = filterWords(
  [
    'Jeg', 'Det', 'Vi', 'kan', 'har', 'skole', 'mor', 'far', 'dag', 'bog',
    'skrive', 'læse', 'Danmark', 'familie', 'arbejde', 'verden', 'god',
    'tekst', 'ven', 'brev', 'plan', 'natur', 'lektion', 'dansk', 'sommer',
    'Freja', 'Axel', 'Mette', 'Ole', 'Jesper', 'Gitte', 'Hjalte', 'Ida',
  ],
  t7.unlockedKeys,
);

export const NIVEAU_5_TRINS: Trin[] = [t1, t2, t3, t4, t5, t6, t7];

export const NIVEAU_5_TIER: CurriculumTier = {
  id: 'niveau-5',
  title: 'Niveau 5',
  description: 'Shift, store bogstaver og de vigtigste tegn.',
  trins: NIVEAU_5_TRINS,
};
